// Core shared types for HOTGAMES rooms (server + in-browser local host).

export type Lang = "ka" | "en" | "ru";
export const LANGS: Lang[] = ["ka", "en", "ru"];

export type GameId = "bluff" | "spy" | "match" | "truf" | "guess";
export const ROOM_GAMES: GameId[] = ["bluff", "spy", "match", "truf", "guess"];

export type Role = "player" | "audience";

/** Error codes are translated on the client (i18n key `err.<code>`). */
export type ErrorCode =
  | "not_found"
  | "not_host"
  | "not_player"
  | "bad_phase"
  | "bad_input"
  | "too_few_players"
  | "room_full"
  | "name_taken"
  | "kicked"
  | "rate_limited"
  | "is_truth"
  | "too_close"
  | "taken"
  | "own_option"
  | "already"
  | "not_your_turn"
  | "no_help_left"
  | "self_vote"
  | "no_content"
  | "paused"
  | "avatar_taken"
  | "server";

export interface PlayerRec {
  id: string;
  name: string;
  color: number; // index into PLAYER_COLORS
  avatar: number; // index into AVATARS
  role: Role;
  isBot: boolean;
  connected: boolean;
  joinedAt: number;
  lastSeen: number;
  userId?: number | null; // registered account (server only)
}

/** What every client learns about a player. */
export interface PlayerPublic {
  id: string;
  name: string;
  color: number;
  avatar: number;
  role: Role;
  isBot: boolean;
  isHost: boolean;
  connected: boolean;
}

export type RoomPhase = "lobby" | "game";

export interface RoomCore<Settings = any, GameState = any> {
  code: string;
  game: GameId;
  lang: Lang;
  local: boolean; // in-browser (pass-and-play / solo / demo)
  /** server rooms: cryptographic randomness instead of the seeded (reproducible) generator */
  secure?: boolean;
  createdAt: number;
  updatedAt: number;
  version: number;
  rngState: number;
  hostId: string;
  players: PlayerRec[];
  settings: Settings;
  phase: RoomPhase;
  state: GameState | null;
  paused: { at: number } | null;
  round: number; // how many games were started in this room (play again)
  usedContent: string[]; // content ids already played in this room (avoid repeats)
  kicked: string[]; // names kicked from the room (cannot rejoin with same token)
}

/** The full snapshot a client renders. */
export interface RoomView<GameView = any, Settings = any> {
  code: string;
  game: GameId;
  lang: Lang;
  local: boolean;
  version: number;
  serverNow: number;
  hostId: string;
  me: { id: string; role: Role; isHost: boolean } | null;
  players: PlayerPublic[];
  settings: Settings;
  phase: RoomPhase;
  paused: boolean;
  round: number;
  view: GameView | null; // engine projection for this viewer
}

export type RoomAction =
  | { t: "settings"; settings: unknown }
  | { t: "start" }
  | { t: "again" }
  | { t: "lobby" }
  | { t: "kick"; id: string }
  | { t: "addBot" }
  | { t: "removeBot"; id: string }
  | { t: "rename"; name: string }
  | { t: "avatar"; avatar: number; color?: number }
  | { t: "role"; role: Role }
  | { t: "pause" }
  | { t: "resume" }
  | { t: "host"; id: string }
  | { t: "leave" }
  | { t: "g"; a: any };

export type ActResult = { ok: true } | { ok: false; error: ErrorCode };

export interface GameResultSummary {
  game: GameId;
  lang: Lang;
  players: { id: string; name: string; score: number; userId?: number | null; isBot: boolean }[];
  winnerIds: string[];
}
